import { useState, useEffect } from 'react';
import LocalStorage from '../config/LocalStorage';
import {
    getVersionWithoutBuildNumber,
    getMainVersions,
    getBetaVersions,
    getRCVersions,
    buildVersionedUrl
} from '../helper/helper';
import { APP_VERSION } from '../variables/staticValue';

let pendingVersionCheck = null;

const fetchAllVersions = async () => {
    if (pendingVersionCheck) {
        return pendingVersionCheck;
    }

    pendingVersionCheck = Promise.all([
        getMainVersions(),
        getBetaVersions(),
        getRCVersions()
    ]).then(([mainVersions, betaVersions, rcVersions]) => {
        return [...mainVersions, ...betaVersions, ...rcVersions];
    }).finally(() => {
        pendingVersionCheck = null;
    });

    return pendingVersionCheck;
};

const isVersionInList = (version, allVersions) => {
    const cleanVersion = getVersionWithoutBuildNumber(version);
    if (allVersions.some(v => getVersionWithoutBuildNumber(v) === cleanVersion)) return true;

    const versionBase = cleanVersion.split('-')[0];
    return allVersions.some(v => {
        const vBase = getVersionWithoutBuildNumber(v).split('-')[0];
        if (vBase === versionBase) {
            if (version.includes('-beta') && v.includes('-beta')) return true;
            if (version.includes('-rc') && v.includes('-rc')) return true;
            if (!version.includes('-') && !v.includes('-')) return true;
        }
        return false;
    });
};

const checkVersionExists = async (version) => {
    try {
        const allVersions = await fetchAllVersions();
        return isVersionInList(version, allVersions);
    } catch (error) {
        console.warn("Failed to check version existence:", error);
        return false;
    }
};

const getLatestAvailableVersion = async () => {
    try {
        const allVersions = await fetchAllVersions();

        const mainVersions = allVersions.filter(v => !v.includes('-'));
        if (mainVersions.length > 0) {
            return getVersionWithoutBuildNumber(mainVersions[0]);
        }

        const betaVersions = allVersions.filter(v => v.includes('-beta'));
        if (betaVersions.length > 0) {
            return getVersionWithoutBuildNumber(betaVersions[0]);
        }

        const rcVersions = allVersions.filter(v => v.includes('-rc'));
        if (rcVersions.length > 0) {
            return getVersionWithoutBuildNumber(rcVersions[0]);
        }

        return allVersions[0] ? getVersionWithoutBuildNumber(allVersions[0]) : null;
    } catch {
        return null;
    }
};

export function useVersionRedirect() {
    const [isRedirecting, setIsRedirecting] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const handleVersionRedirect = async () => {
            const pathname = window.location.pathname;
            const isRootPath = pathname === '' || pathname === '/';
            const currentVersion = getVersionWithoutBuildNumber(APP_VERSION);

            if (isRootPath) {
                const rawLastUsedVersion = LocalStorage.getLastUsedVersion();
                const lastUsedVersion = rawLastUsedVersion ? getVersionWithoutBuildNumber(rawLastUsedVersion) : null;
                const search = window.location.search;

                if (lastUsedVersion) {
                    const isVersionValid = await checkVersionExists(lastUsedVersion);
                    if (cancelled) return;
                    if (isVersionValid) {
                        window.location.href = window.location.origin + '/' + lastUsedVersion + search;
                        return;
                    }
                }

                const isCurrentVersionValid = await checkVersionExists(currentVersion);
                if (cancelled) return;
                if (isCurrentVersionValid) {
                    LocalStorage.setLastUsedVersion(currentVersion);
                    window.location.href = window.location.origin + '/' + currentVersion + search;
                    return;
                }

                const latestVersion = await getLatestAvailableVersion();
                if (cancelled) return;
                if (latestVersion) {
                    LocalStorage.setLastUsedVersion(latestVersion);
                    window.location.href = window.location.origin + '/' + latestVersion + search;
                    return;
                }

                console.error('No available version found on server');
                setIsRedirecting(false);
            } else {
                // User navigated to a versioned path (e.g., /1.16.0/home)
                const versionFromPath = pathname.split('/')[1];
                if (versionFromPath && /^\d+\.\d+\.\d+(-[a-zA-Z0-9.]+)?/.test(versionFromPath)) {
                    const isVersionValid = await checkVersionExists(versionFromPath);
                    if (cancelled) return;

                    if (isVersionValid) {
                        // Version exists - save preference
                        LocalStorage.setLastUsedVersion(versionFromPath);
                    } else {
                        // Version doesn't exist - redirect to valid version
                        console.warn(`Version ${versionFromPath} does not exist, redirecting to valid version...`);

                        // Try last used version first
                        const rawLastUsed = LocalStorage.getLastUsedVersion();
                        const lastUsedVersion = rawLastUsed ? getVersionWithoutBuildNumber(rawLastUsed) : null;
                        if (lastUsedVersion && lastUsedVersion !== versionFromPath) {
                            const isLastUsedValid = await checkVersionExists(lastUsedVersion);
                            if (cancelled) return;
                            if (isLastUsedValid) {
                                window.location.href = buildVersionedUrl(lastUsedVersion);
                                return;
                            }
                        }

                        // Fall back to latest available version
                        const latestVersion = await getLatestAvailableVersion();
                        if (cancelled) return;
                        if (latestVersion) {
                            LocalStorage.setLastUsedVersion(latestVersion);
                            window.location.href = buildVersionedUrl(latestVersion);
                            return;
                        }

                        // No valid version found
                        console.error('No available version found on server');
                    }
                }
            }

            if (!cancelled) {
                setIsRedirecting(false);
            }
        };

        handleVersionRedirect();

        return () => {
            cancelled = true;
        };
    }, []);

    return { isRedirecting };
}
